
import { defineComponent } from 'vue'
import { mapActions, mapGetters } from 'vuex'
import Analyzer from './Analyzer.vue'
import AnalyzerSearch from './AnalyzerSearch.vue'

import {
  AnalyzerInterface,
  AnalyzerListActions,
  AnalyzerListGetters,
  TransformerInterface
} from '~/store/analyzer/list'
import { RepoConfigAnalyzerMeta, RepoConfigInterface } from '~/store/repository/detail'

interface AnalyzerComponent extends Vue {
  validateConfig: (collapseCardsIfRequired?: boolean) => number
}

export default defineComponent({
  name: 'ConfigAnalyzerSelector',

  components: {
    Analyzer,
    AnalyzerSearch
  },

  props: {
    userConfig: {
      type: Object as () => RepoConfigInterface,
      default: () => ({})
    },
    isProcessing: {
      type: Boolean,
      default: false
    },
    disableAnalyzerCardActions: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      showAnalyzerList: false
    }
  },

  computed: {
    ...mapGetters('analyzer/list', {
      analyzerList: AnalyzerListGetters.ANALYZERS
    }),

    selectedTransformers() {
      return this.userConfig.code_formatters ?? this.userConfig.transformers ?? []
    },

    selectedAnalyzers(): string[] {
      return this.userConfig.analyzers.map((analyzer) => analyzer.name)
    },

    activeAnalyzers(): AnalyzerInterface[] {
      return (this.analyzerList as AnalyzerInterface[])
        .filter((config) => this.selectedAnalyzers.includes(config.name))
        .sort((curr, next) => {
          const currIndex = this.selectedAnalyzers.indexOf(curr.name)
          const nextIndex = this.selectedAnalyzers.indexOf(next.name)
          return currIndex - nextIndex
        })
    }
  },

  methods: {
    ...mapActions('analyzer/list', {
      fetchAnalyzers: AnalyzerListActions.FETCH_ANALYZER_LIST
    }),

    addAnalyzer(analyzer: AnalyzerInterface): void {
      analyzer.enabled = true
      this.$emit('updateAnalyzers', {
        name: analyzer.shortcode,
        meta: {},
        enabled: true,
        type: analyzer.analyzerType
      })
    },

    removeAnalyzer(analyzer: AnalyzerInterface): void {
      analyzer.enabled = false
      this.$emit('updateAnalyzers', {
        name: analyzer.shortcode,
        meta: {},
        enabled: false
      })
    },

    validateConfig(collapseCardsIfRequired = true): boolean {
      let issueCount = 0
      this.$children.forEach((child: unknown) => {
        const childProxy = child as AnalyzerComponent

        if (childProxy.validateConfig) {
          issueCount = issueCount + childProxy.validateConfig(collapseCardsIfRequired)
        }
      })

      return !(issueCount > 0)
    },

    syncAnalyzer(obj: AnalyzerInterface): void {
      this.$emit('updateAnalyzers', obj)
    },

    syncTransformers(obj: Record<string, TransformerInterface>): void {
      this.$emit('updateTransformers', obj)
    },

    getConfig(name: string): RepoConfigAnalyzerMeta | undefined {
      const filteredList = this.userConfig.analyzers.filter((config) => config.name === name)
      return filteredList.length ? filteredList[0] : undefined
    }
  },

  async fetch() {
    await this.fetchAnalyzers()
  }
})
